import React, { useEffect, useRef, useState } from 'react';
import { FileText, MapPin, SlidersHorizontal, X, Zap } from 'lucide-react';
import { cn } from '../../../lib/utils';
import { Badge } from '../../ui/badge';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import type { RccHandlers, RccJobColumn, TFn } from './rccTypes';
import type { RccTheme } from './rcc-themes';

export function countActiveFilters(col: RccJobColumn) {
  let n = col.activeLocations.length + col.customLocations.length + col.skills.length;
  if (col.languageFilter) n += 1;
  return n;
}

/** Icon-prefixed filter rows (location / skills / CV language) for the popover. */
function RccColumnFilters({ col, t, h }: { col: RccJobColumn; t: TFn; h: RccHandlers }) {
  const [locInput, setLocInput] = useState('');
  const [skillInput, setSkillInput] = useState('');
  const chipActive = 'border-slate-300 bg-slate-100 text-slate-800';
  const langActive = 'border-slate-300 bg-slate-100 text-slate-800';

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-1.5">
        <MapPin className="size-3.5 shrink-0 text-slate-400" />
        {col.allLocations.map((loc) => {
          const active = col.activeLocations.includes(loc);
          return (
            <button
              key={loc}
              type="button"
              onClick={() => h.toggleLocation(col.id, loc)}
              className={`rounded-md border px-2 py-0.5 text-xs font-medium transition-colors ${
                active
                  ? chipActive
                  : 'border-slate-200 text-slate-500 hover:border-slate-300 hover:bg-slate-50'
              }`}
            >
              {loc}
            </button>
          );
        })}
        {col.customLocations.map((loc) => (
          <span
            key={`custom-${loc}`}
            className="inline-flex items-center gap-1 rounded-md border border-slate-300 bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-700"
          >
            {loc}
            <button type="button" className="rounded p-0.5 hover:bg-slate-200" onClick={() => h.removeCustomLocation(col.id, loc)}>
              <X className="size-3" />
            </button>
          </span>
        ))}
        <Input
          value={locInput}
          onChange={(e) => setLocInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && locInput.trim()) {
              h.addCustomLocation(col.id, locInput.trim());
              setLocInput('');
            }
          }}
          placeholder={t('column.location_placeholder')}
          className="h-6 w-24 rounded-md border-dashed text-xs"
        />
      </div>

      <div className="flex flex-wrap items-center gap-1.5">
        <Zap className="size-3.5 shrink-0 text-slate-400" />
        {col.skills.map((skill) => (
          <span
            key={skill}
            className="inline-flex items-center gap-1 rounded-md border border-slate-300 bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-700"
          >
            {skill}
            <button type="button" className="rounded p-0.5 hover:bg-slate-200" onClick={() => h.removeSkill(col.id, skill)}>
              <X className="size-3" />
            </button>
          </span>
        ))}
        <Input
          value={skillInput}
          onChange={(e) => setSkillInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && skillInput.trim()) {
              h.addSkill(col.id, skillInput.trim());
              setSkillInput('');
            }
          }}
          placeholder={col.skills.length ? t('column.skills_add') : t('column.skills_placeholder')}
          className="h-6 min-w-[110px] flex-1 rounded-md border-dashed text-xs"
        />
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <FileText className="size-3.5 shrink-0 text-slate-400" />
        {(['DE', 'EN'] as const).map((lang) => (
          <button
            key={lang}
            type="button"
            onClick={() => h.toggleLanguageFilter(col.id, lang)}
            className={`rounded-md border px-2.5 py-0.5 text-xs font-medium ${
              col.languageFilter === lang ? langActive : 'border-slate-200 text-slate-500'
            }`}
          >
            {lang}
          </button>
        ))}
        {col.languageFilter && (
          <button
            type="button"
            className="text-xs text-slate-400 hover:text-slate-600"
            onClick={() => h.toggleLanguageFilter(col.id, col.languageFilter!)}
          >
            {t('column.clear')}
          </button>
        )}
      </div>
    </div>
  );
}

export function RccActiveFilterChips({
  col,
  theme,
  h,
}: {
  col: RccJobColumn;
  theme: RccTheme;
  h: RccHandlers;
}) {
  const chips: { key: string; label: string; onRemove: () => void }[] = [];
  for (const loc of col.activeLocations) {
    chips.push({ key: `loc-${loc}`, label: loc, onRemove: () => h.toggleLocation(col.id, loc) });
  }
  for (const loc of col.customLocations) {
    chips.push({
      key: `custom-${loc}`,
      label: loc,
      onRemove: () => h.removeCustomLocation(col.id, loc),
    });
  }
  for (const skill of col.skills) {
    chips.push({ key: `skill-${skill}`, label: skill, onRemove: () => h.removeSkill(col.id, skill) });
  }
  if (col.languageFilter) {
    chips.push({
      key: 'lang',
      label: col.languageFilter,
      onRemove: () => h.toggleLanguageFilter(col.id, col.languageFilter!),
    });
  }

  if (!chips.length) return null;

  return (
    <div className="flex flex-wrap gap-1.5">
      {chips.map((chip) => (
        <span
          key={chip.key}
          className={cn(
            'inline-flex items-center gap-1 rounded-md border py-0.5 pr-1 pl-2 text-[11px] font-medium text-slate-700',
            theme.chipBorder,
            theme.chip,
          )}
        >
          {chip.label}
          <button
            type="button"
            onClick={chip.onRemove}
            className={cn('rounded p-0.5', theme.chipHover)}
            aria-label={`Remove ${chip.label}`}
          >
            <X className="size-3" />
          </button>
        </span>
      ))}
    </div>
  );
}

export function RccFilterMenu({
  col,
  filterCount,
  theme,
  t,
  h,
}: {
  col: RccJobColumn;
  filterCount: number;
  theme: RccTheme;
  t: TFn;
  h: RccHandlers;
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setOpen(false);
  }, [col.title]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    const onPointerDown = (e: PointerEvent) => {
      if (rootRef.current?.contains(e.target as Node)) return;
      setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('pointerdown', onPointerDown);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('pointerdown', onPointerDown);
    };
  }, [open]);

  return (
    <div ref={rootRef} className="relative">
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-haspopup="dialog"
        className="gap-1.5"
      >
        <SlidersHorizontal className="size-3.5" />
        {t('command_center.filters')}
        {filterCount > 0 && (
          <Badge className="h-4 min-w-4 bg-slate-800 px-1 text-[10px] tabular-nums">
            {filterCount}
          </Badge>
        )}
      </Button>
      {open && (
        <div
          role="dialog"
          aria-label={t('command_center.filters')}
          className={cn(
            'absolute right-0 top-[calc(100%+6px)] z-50 max-h-[min(380px,55vh)] w-[min(300px,calc(100vw-2rem))] overflow-y-auto rounded-xl border p-3 shadow-lg',
            theme.border,
            theme.popover,
          )}
        >
          <RccColumnFilters col={col} t={t} h={h} />
        </div>
      )}
    </div>
  );
}
